import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { completeSocialLink, type CompleteOutcome } from '@/lib/accountLink';

/**
 * L'atterraggio del «Collega Telegram»: Telegram riporta qui con `code` e `state`,
 * e questa schermata chiude il giro con l'Account API e rimanda in Accounts.
 *
 * ⚠️ **Non e' `/auth/callback`, ed e' voluto.** Quella rotta e' il ritorno del LOGIN:
 * `main.tsx` la intercetta PRIMA del bootstrap (fast path senza Router ne' Logto) per
 * far girare il ponte del browser di sistema. Questa invece si raggiunge solo da una
 * app gia' avviata e autenticata, quindi ha Router, i18n e i token al loro posto.
 * Tenerle separate significa che nessun errore di qui puo' cadere sul login, che e' il
 * percorso piu' caricato dell'applicazione.
 *
 * 🔴 **Cosa NON e' stato misurato, e chi lo misurera'**: che `oauth.telegram.org` si
 * lasci aprire DENTRO la WebView di Telegram su mobile. Il login usa il ponte al
 * browser di sistema per Google, che rifiuta le WebView (`disallowed_useragent`) — ma
 * quella e' una misura su Google, non su Telegram, e non si trasporta. Qui si fa la
 * cosa semplice su tutte e due le piattaforme (redirect dentro la WebView) perche' il
 * ponte e' una macchina intera, e il registro dei tipi di `startParamRoutes.ts` dice
 * di non coniare un tipo prima che abbia un consumatore vero. Se la prova sul telefono
 * mostra che Telegram rifiuta la WebView, il ponte diventa il seguito — con una
 * ragione misurata invece che con un sospetto.
 */
export function AuthLinkCallback() {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<CompleteOutcome | 'in-corso'>('in-corso');

  // I parametri del callback, presi UNA volta: `completeSocialLink` li rimanda a
  // Logto come `connectorData` cosi' come sono arrivati — chi smista non interpreta
  // il carico (stessa regola del registro degli start_param).
  const params = useMemo(
    () => Object.fromEntries(new URLSearchParams(window.location.search).entries()),
    [],
  );

  useEffect(() => {
    let annullato = false;
    void completeSocialLink(params).then((esito) => {
      if (annullato) return;
      setOutcome(esito);
      // Solo il successo torna da solo: su un errore la schermata resta, perche'
      // un rimbalzo silenzioso in Accounts mostrerebbe la riga «non collegato»
      // senza che nessuno sappia che un tentativo c'e' stato.
      if (esito === 'linked') {
        navigate('/settings/accounts', { replace: true });
      }
    });
    return () => {
      annullato = true;
    };
  }, [params, navigate]);

  const messaggio =
    outcome === 'in-corso'
      ? t('social.link_in_progress', { defaultValue: 'Collegamento in corso…' })
      : outcome === 'linked'
        ? t('social.link_done', { defaultValue: 'Collegato.' })
        : outcome === 'state-mismatch'
          ? t('social.link_mismatch', {
              defaultValue: 'Questo ritorno non appartiene alla richiesta aperta da questa app.',
            })
          : outcome === 'no-pending'
            ? t('social.link_expired', {
                defaultValue: 'La richiesta è scaduta o è già stata completata.',
              })
            : t('social.link_failed', { defaultValue: 'Collegamento non riuscito.' });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg p-6">
      <p className="text-center text-sm text-fg-muted">{messaggio}</p>
      {outcome !== 'in-corso' && outcome !== 'linked' && (
        <button
          onClick={() => navigate('/settings/accounts', { replace: true })}
          className="rounded-md border border-border px-4 py-2 text-sm text-fg"
        >
          {t('social.back_to_accounts', { defaultValue: 'Torna agli account' })}
        </button>
      )}
    </div>
  );
}
